import { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  CardContent
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridSortModel, GridPaginationModel } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Plus, UserPlus, Search, RotateCcw } from 'lucide-react'; 
import { Person } from '../../types/person';
import { PersonService, PersonFilter } from '../../services/personService';
import { useTheme } from '@mui/material/styles';
import { ptBRGridLocale, getDataGridStyles } from '../../utils/gridLocalization';
import axios from 'axios';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import SuccessSnackbar from '../../components/common/SuccessSnackbar';

const PersonListPage = () => {
  const [persons, setPersons] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [personToDelete, setPersonToDelete] = useState<Person | null>(null);
  
  // Estados para filtros
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'true' | 'false'>('all');
  
  // Estado para paginação unificado
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
  const [totalRowCount, setTotalRowCount] = useState(0); // Estado para contagem
  
  // Estado para ordenação
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'name',
      sort: 'asc',
    },
  ]);

  // <<< Adicionar estados para o Snackbar de Sucesso >>>
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const navigate = useNavigate();
  const theme = useTheme();
  const personService = useMemo(() => new PersonService(), []); // Usar useMemo

  useEffect(() => {
    fetchPersons();
  }, [paginationModel.page, paginationModel.pageSize, sortModel]);

  const fetchPersons = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: PersonFilter = {
        page: paginationModel.page + 1, 
        pageSize: paginationModel.pageSize,
        name: searchText || undefined,
        active: statusFilter === 'all' ? undefined : statusFilter === 'true', 
      };
      
      if (sortModel.length > 0) {
        filters.sortField = sortModel[0].field;
        filters.sortOrder = sortModel[0].sort === 'asc' ? 'asc' : 'desc';
      }
      
      const response = await personService.getAll(filters);
      setPersons(response.data);
      setTotalRowCount(response.totalCount);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Falha ao carregar pessoas. Tente novamente mais tarde.');
      setPersons([]);
      setTotalRowCount(0);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    navigate('/pessoas/new');
  };

  const handleEdit = (id: number) => {
    navigate(`/pessoas/edit/${id}`);
  };

  const handleDeleteClick = (person: Person) => {
    setPersonToDelete(person);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!personToDelete) return;
    
    try {
      await personService.delete(personToDelete.id);
      await fetchPersons();
      setDeleteDialogOpen(false);
      setPersonToDelete(null);
      // <<< Mostrar Snackbar de sucesso >>>
      setSnackbarMessage('Pessoa excluída com sucesso.');
      setSnackbarOpen(true);
    } catch (err: any) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setError(err.response.data?.message || 'Não é possível excluir: pessoa possui contratos associados.');
      } else {
        setError(err.response?.data?.message || err.message || 'Falha ao excluir pessoa. Tente novamente.');
      }
      console.error(err);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPersonToDelete(null);
  };

  // Handlers para filtros
  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value as 'all' | 'true' | 'false');
  };

  const handleSearch = async () => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    fetchPersons();
  };

  const handleClear = async () => {
    setSearchText('');
    setStatusFilter('all');
    setPaginationModel({ page: 0, pageSize: 10 });
      };
      
  // Handler unificado para paginação
  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  // Manipula mudanças na ordenação
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  };

  // <<< Adicionar handler para fechar o Snackbar >>>
  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Nome Completo',
      flex: 2,
      minWidth: 250,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 80,
      renderCell: (params: GridRenderCellParams) => (
          <Chip 
            label={params.value ? 'Ativo' : 'Inativo'} 
            color={params.value ? 'success' : 'error'}
            size="small"
            sx={{ 
              fontSize: '0.75rem', 
              height: '22px',
              fontWeight: 'bold',
              color: 'white',
              '& .MuiChip-label': {
                padding: '0px 8px'
              }
            }}
          />
      ),
    },
    {
      field: 'profile',
      headerName: 'Perfil',
      width: 150,
      valueGetter: (params) => params.row.profile?.name || '-',
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Button
            onClick={() => handleEdit(params.row.id)}
            sx={{ minWidth: 'auto', mr: 0.5, p: '4px' }}
            title="Editar"
            size="small"
          >
            <Edit size={18} />
          </Button>
          <Button
            onClick={() => handleDeleteClick(params.row)}
            color="error"
            sx={{ minWidth: 'auto', p: '4px' }}
            title="Excluir"
            disabled={!params.row.active}
            size="small"
          >
            <Trash2 size={18} />
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}
      >
        <Typography variant="h5" component="h1">
          Pessoas
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<UserPlus size={16} />} 
          onClick={handleAddNew}
          size="small"
          sx={{ 
            py: 0.5, 
            px: 2, 
            height: 36, 
            fontWeight: 'bold',
            fontSize: '0.75rem',
            borderRadius: 1
          }}
        >
          NOVA PESSOA
        </Button>
      </Box>

      <Box sx={{ 
          mb: 1.5, 
          p: 1,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.shape.borderRadius
      }}>
        <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={6} md={5}>
                <TextField
                    fullWidth 
                    label="Buscar por Nome ou Email"
                    variant="outlined" 
                    value={searchText} 
                    onChange={handleSearchTextChange} 
                    size="small"
                    margin="dense"
                    InputProps={{ 
                        style: { fontSize: '0.85rem' },
                        sx: { py: 0.3, height: 36 },
                        startAdornment: <Search size={16} style={{ marginRight: 8, color: '#666' }} />,
                    }}
                    InputLabelProps={{ 
                        style: { fontSize: '0.85rem' } 
                    }}
                    placeholder="Buscar por Nome ou Email"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" margin="dense">
                    <InputLabel id="status-filter-label" sx={{ fontSize: '0.85rem' }}>Status</InputLabel>
                    <Select
                        labelId="status-filter-label" 
                        value={statusFilter} 
                        label="Status"
                        onChange={handleStatusFilterChange}
                        sx={{ 
                            fontSize: '0.85rem',
                            '.MuiSelect-select': { py: 0.5 },
                            height: 36
                        }}
                    >
                        <MenuItem value="all" sx={{ fontSize: '0.85rem' }}>Todos</MenuItem>
                        <MenuItem value="true" sx={{ fontSize: '0.85rem' }}>Ativo</MenuItem>
                        <MenuItem value="false" sx={{ fontSize: '0.85rem' }}>Inativo</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                    <Button
                        variant="contained"
                        onClick={handleSearch}
                        size="small"
                        sx={{ 
                            py: 0.5, 
                            px: 2,
                            height: 36,
                            fontWeight: 'bold', 
                            fontSize: '0.75rem',
                            borderRadius: 1
                        }}
                        startIcon={<Search size={16} />}
                    >
                        BUSCAR
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleClear}
                        size="small"
                        sx={{ 
                            py: 0.5, 
                            px: 2,
                            height: 36,
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            borderRadius: 1
                        }}
                        startIcon={<RotateCcw size={16} />}
                    >
                        LIMPAR
                    </Button>
                </Stack>
            </Grid>
        </Grid>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0, fontSize: '0.875rem' }}>
          {error}
        </Alert>
      )}

      <Card elevation={2}>
        <CardContent>
      <DataGrid
        rows={persons}
        columns={columns}
            rowCount={totalRowCount}
        loading={loading}
            paginationMode="server"
            onPaginationModelChange={handlePaginationModelChange}
            paginationModel={paginationModel}
        pageSizeOptions={[5, 10, 25, 50]}
            sortingMode="server"
            onSortModelChange={handleSortModelChange}
        sortModel={sortModel}
        localeText={ptBRGridLocale}
        sx={getDataGridStyles(theme)}
        density="compact"
        autoHeight
        disableRowSelectionOnClick
      />
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        PaperProps={{
          sx: {
            width: '100%'
          }
        }}
      >
        <DialogTitle sx={{ fontSize: '1rem', pt: 1.5, pb: 1 }}>Confirmar Exclusão</DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          <DialogContentText sx={{ fontSize: '0.9rem' }}>
            Tem certeza que deseja excluir permanentemente a pessoa "{personToDelete?.name}"?
            Esta ação não pode ser desfeita e removerá todos os dados da pessoa.
            {error && <Alert severity="error" sx={{ mt: 2, fontSize: '0.85rem' }}>{error}</Alert>}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={handleDeleteCancel} size="small">Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus size="small">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
      {/* <<< Adicionar o componente Snackbar >>> */}
      <SuccessSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default PersonListPage; 